import { useState, useEffect } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { request } from '@/lib/request'
import { usePersistentState } from '@/hooks/usePersistentState'

interface BlockData {
  timestamp: number
  codes: string[]
  resultCodes: string
  datas: Record<string, number>
}

const codes = ['20', '21', '22', '23']

export default function Block() {
  const [tm, setTm] = usePersistentState('admin:block:time-range', '1h')
  const [datas, setDatas] = useState<BlockData[]>([])
  const [loading, setLoading] = useState(false)
  const [sortOrder, setSortOrder] = usePersistentState<'asc' | 'desc'>(
    'admin:block:sort-order',
    'desc',
  )

  const formatDate = (timestamp: number) => {
    const date = new Date(timestamp)
    const MM = String(date.getMonth() + 1).padStart(2, '0')
    const dd = String(date.getDate()).padStart(2, '0')
    const hh = String(date.getHours()).padStart(2, '0')
    const mm = String(date.getMinutes()).padStart(2, '0')
    return `${MM}-${dd} ${hh}:${mm}`
  }

  const getData = async () => {
    setLoading(true)
    try {
      // 使用和trend完全相同的接口
      const resp = await request('/common/apis/v1/trend', {
        plat: 'xr',
        h: tm,
      })

      console.log('Block API response:', resp)

      // 处理ES数据格式
      if (resp?.data?.hits?.hits) {
        const resData = resp.data.hits.hits
          .map((hit: any) => hit._source)
          .sort((a: any, b: any) => {
            return sortOrder === 'asc'
              ? a.timestamp - b.timestamp
              : b.timestamp - a.timestamp
          })

        console.log('Processed data count:', resData.length)
        setDatas(resData)
      } else if ((resp as any)?.hits?.hits) {
        const resData = (resp as any).hits.hits
          .map((hit: any) => hit._source)
          .sort((a: any, b: any) => {
            return sortOrder === 'asc'
              ? a.timestamp - b.timestamp
              : b.timestamp - a.timestamp
          })

        console.log('Processed data count:', resData.length)
        setDatas(resData)
      } else {
        console.error('Unexpected data format:', resp)
      }
    } catch (error) {
      console.error('Failed to fetch block data:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    getData()
  }, [sortOrder])

  // 计算总计
  const totals = codes.reduce((acc, code) => {
    acc[code] = datas.reduce((sum, item) => sum + (item.datas[code] || 0), 0)
    acc[code + '_c'] = datas.reduce((sum, item) => sum + (item.datas[code + '_c'] || 0), 0)
    acc[code + '_avg'] = datas.reduce((sum, item) => {
      const base = item.datas[code] || 0
      const count = item.datas[code + '_c'] || 1
      return sum + (base / count)
    }, 0)
    return acc
  }, {} as Record<string, number>)

  return (
    <div className="h-full p-6 overflow-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">Block 数据</h1>

        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-muted-foreground">时间:</span>
          <Input
            className="w-[120px] h-9"
            value={tm}
            onChange={(e) => setTm(e.target.value)}
            placeholder="如: 1h, 3h"
          />
          <span className="text-sm font-medium text-muted-foreground">排序:</span>
          <Select value={sortOrder} onValueChange={(value: 'asc' | 'desc') => setSortOrder(value)}>
            <SelectTrigger className="w-[100px] h-9">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="desc">逆序</SelectItem>
              <SelectItem value="asc">正序</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={getData} disabled={loading} size="sm">
            {loading ? '加载中...' : '获取'}
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader className="py-4">
          <CardTitle className="text-base">数据列表 ({datas.length} 条)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-1 overflow-x-auto">
            {/* 表头 */}
            <div className="flex items-center gap-3 py-2 px-3 bg-muted/50 rounded-md font-medium text-sm sticky top-0 min-w-max">
              <div className="w-[140px] min-w-[140px] text-center flex-shrink-0 whitespace-nowrap">时间</div>
              <div className="w-[180px] min-w-[180px] text-center flex-shrink-0 whitespace-nowrap">Codes</div>
              {codes.map((code) => (
                <div key={code} className="w-[200px] min-w-[200px] text-right font-mono flex-shrink-0 whitespace-nowrap">
                  {code}
                </div>
              ))}
            </div>

            {/* 数据行 */}
            {datas.map((data, index) => (
              <div
                key={`${data.timestamp}-${index}`}
                className="flex items-center gap-3 py-2 px-3 text-sm hover:bg-muted/30 rounded-md transition-colors min-w-max"
              >
                <div className="w-[140px] min-w-[140px] text-center text-muted-foreground flex-shrink-0 whitespace-nowrap">
                  {formatDate(data.timestamp)}
                </div>
                <div className="w-[180px] min-w-[180px] text-center font-mono flex-shrink-0 whitespace-nowrap">
                  <span className="text-xs whitespace-nowrap">
                    {data.codes?.join(' ')}
                    <span className="text-muted-foreground ml-1 whitespace-nowrap">
                      [{data.codes?.reduce((sum, c) => sum + parseInt(c), 0)}]
                    </span>
                  </span>
                </div>
                {codes.map((code) => {
                  const isHit = data.resultCodes?.indexOf(code) > -1
                  const base = data.datas[code] || 0
                  const count = data.datas[code + '_c'] || 0
                  const avg = count > 0 ? (base / count).toFixed(2) : '0.00'

                  return (
                    <div
                      key={code}
                      className={`w-[200px] min-w-[200px] text-right font-mono text-xs flex-shrink-0 whitespace-nowrap ${
                        isHit ? 'bg-red-100 dark:bg-red-900/30 px-2 py-1 rounded' : ''
                      }`}
                    >
                      <span className={`whitespace-nowrap ${isHit ? 'font-semibold' : ''}`}>
                        {base.toLocaleString()}
                      </span>
                      <span className="text-muted-foreground whitespace-nowrap">
                        ({avg})[{count}]
                      </span>
                    </div>
                  )
                })}
              </div>
            ))}

            {/* 总计行 */}
            {datas.length > 0 && (
              <div className="flex items-center gap-3 py-3 px-3 bg-primary/5 rounded-md font-semibold text-sm border-t-2 border-primary/20 mt-2 min-w-max">
                <div className="w-[140px] min-w-[140px] text-center flex-shrink-0 whitespace-nowrap">总计</div>
                <div className="w-[180px] min-w-[180px] flex-shrink-0 whitespace-nowrap"></div>
                {codes.map((code) => (
                  <div key={code} className="w-[200px] min-w-[200px] text-right font-mono text-xs flex-shrink-0 whitespace-nowrap">
                    <span className="text-primary whitespace-nowrap">
                      {totals[code].toLocaleString()}
                    </span>
                    <span className="text-muted-foreground whitespace-nowrap">
                      ({totals[code + '_avg'].toFixed(2)})[{totals[code + '_c']}]
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {datas.length === 0 && !loading && (
            <div className="text-center py-12 text-muted-foreground">
              暂无数据
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
